/**
 * Client-route helpers.
 *
 * The workspace lives at ``/clients/<email>?as=<cid>``. Every navigation
 * to a specific canonical client goes through ``workspacePath(client)``
 * so we route consistently — the standalone ``/clients/id/<cid>``
 * canonical page has been retired.
 *
 * When a client has no email at all (email-less directory rows), we
 * generate a synthetic placeholder ``<cid>@no-email.local`` so the
 * workspace still has a URL-safe email segment. All clinical reads on
 * that page are scoped by ``?as=<cid>``, so the synthetic email never
 * resolves to real data — it's just a routing carrier.
 */

const SYNTHETIC_EMAIL_SUFFIX = "@no-email.local";

/**
 * Build the workspace URL for a canonical client row.
 * @param {{ _id: string, email?: string } | null} client
 * @returns {string}
 */
export function workspacePath(client) {
  if (!client || !client._id) return "/clients";
  const cid = client._id;
  const email = (client.email || "").trim() || `${cid}${SYNTHETIC_EMAIL_SUFFIX}`;
  return `/clients/${encodeURIComponent(email)}?as=${encodeURIComponent(cid)}`;
}

/**
 * Build the workspace URL for an ``email + cid`` pair. Handy for the
 * directory row where the caller already knows both.
 */
export function workspacePathFor(email, clientId) {
  const cid = String(clientId || "").trim();
  if (!cid) return `/clients/${encodeURIComponent(email || "")}`;
  const e = (email || "").trim() || `${cid}${SYNTHETIC_EMAIL_SUFFIX}`;
  return `/clients/${encodeURIComponent(e)}?as=${encodeURIComponent(cid)}`;
}

/** True when the given email is a routing-only synthetic placeholder. */
export function isSyntheticEmail(email) {
  return typeof email === "string" && email.endsWith(SYNTHETIC_EMAIL_SUFFIX);
}
